import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { PageLoader, LoadingSpinner } from '../../components/common/Loading';
import KycStep1 from '../../components/kyc/KycStep1';
import KycStep2 from '../../components/kyc/KycStep2';
import KycStep3 from '../../components/kyc/KycStep3';
import KycStatus from '../../components/kyc/KycStatus';
import { AuthContext } from '../../context/AuthContext';
import { KYCAPI } from '../../services/api';
import { FiArrowLeft, FiCheck, FiAlertCircle, FiX } from 'react-icons/fi';

const KycVerification = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  
  const countries = [
    'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 
    'France', 'Spain', 'Italy', 'Japan', 'South Korea', 'China', 'India'
  ];

  const [formData, setFormData] = useState({
    document_type: 'passport',
    document_number: '',
    document_country: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: ''
  });

  const [files, setFiles] = useState({
    document_front: null,
    document_back: null,
    selfie_with_document: null,
    proof_of_address: null
  });

  // Fetch KYC status
  useEffect(() => {
    const fetchKycStatus = async () => {
      try {
        setLoading(true);
        const response = await KYCAPI.getKYCStatus();
        
        if (response && response.success !== false) {
          setKycStatus(response);
          
          if (response.status === 'verified' || response.status === 'pending') {
            setStep(4); // Show status page
          }
        }
      } catch (error) {
        // console.error('Error fetching KYC status:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchKycStatus();
    } else {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileChange = (field, file) => {
    if (!file) return;
    
    // Validate file size
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, [field]: 'File must be less than 5MB' }));
      return;
    }
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, [field]: 'Invalid file type' }));
      return;
    }
    
    setFiles(prev => ({ ...prev, [field]: file }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (stepNumber) => {
    const newErrors = {};
    
    switch(stepNumber) {
      case 1:
        if (!formData.document_country) newErrors.document_country = 'Document country is required';
        break;
        
      case 2:
        if (!formData.document_type) newErrors.document_type = 'Document type is required';
        if (!formData.document_number.trim()) newErrors.document_number = 'Document number is required';
        if (!files.document_front) newErrors.document_front = 'Document front is required';
        if (formData.document_type !== 'passport' && !files.document_back) {
          newErrors.document_back = 'Document back is required';
        }
        if (!files.selfie_with_document) newErrors.selfie_with_document = 'Selfie with document is required';
        break;
        
      case 3:
        if (!formData.address_line1.trim()) newErrors.address_line1 = 'Address is required';
        if (!formData.city.trim()) newErrors.city = 'City is required';
        if (!formData.state.trim()) newErrors.state = 'State is required';
        if (!formData.postal_code.trim()) newErrors.postal_code = 'Postal code is required';
        if (!formData.country) newErrors.country = 'Country is required';
        if (!files.proof_of_address) newErrors.proof_of_address = 'Proof of address is required';
        break;
    }
    
    return newErrors;
  };

  const nextStep = () => {
    const stepErrors = validateStep(step);
    
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    
    setStep(prev => Math.min(prev + 1, 3));
    setErrors({});
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
    setErrors({});
  };

  const handleSubmit = async () => {
    const step1Errors = validateStep(1);
    const step2Errors = validateStep(2);
    const step3Errors = validateStep(3);
    
    const combinedErrors = { ...step1Errors, ...step2Errors, ...step3Errors };
    
    if (Object.keys(combinedErrors).length > 0) {
      setErrors(combinedErrors);
      if (Object.keys(step1Errors).length > 0) setStep(1);
      else if (Object.keys(step2Errors).length > 0) setStep(2);
      else setStep(3);
      return;
    }
    
    try {
      setSubmitting(true);
      
      const submitFormData = new FormData();
      
      // Add form data
      Object.keys(formData).forEach(key => {
        submitFormData.append(key, formData[key]);
      });
      
      // Add files
      Object.keys(files).forEach(key => {
        if (files[key]) {
          submitFormData.append(key, files[key]);
        }
      });
      
      // console.log('Submitting KYC...');
      const response = await KYCAPI.submitKYC(submitFormData);
      // console.log('KYC response:', response);
      
      if (response && response.success !== false) {
        setKycStatus({
          status: 'pending',
          submitted_at: new Date().toISOString(),
          message: response.message || 'KYC submitted successfully'
        });
        setStep(4);
      } else {
        setErrors({ 
          general: response.error || response.message || 'Submission failed' 
        });
      }
      
    } catch (error) {
      // console.error('KYC error:', error);
      setErrors({ 
        general: error.message || error.error || 'Network error' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <PageLoader text="Loading KYC information..." />;
  }

  // If already submitted/verified, show status
  if (step === 4 && kycStatus) {
    return (
      <div className="min-h-screen bg-deep-black pt-16">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          >
            <FiArrowLeft className="w-5 h-5" />
            Back to Profile
          </button>
          
          <div className="text-center">
            {kycStatus.status === 'verified' ? (
              <>
                <div className="w-24 h-24 mx-auto rounded-full bg-green-900/30 border-2 border-green-800 flex items-center justify-center mb-6">
                  <FiCheck className="w-12 h-12 text-green-400" />
                </div>
                <h1 className="text-3xl font-bold text-white mb-4">✅ Verification Complete!</h1>
                <p className="text-gray-300 text-lg mb-8">
                  Your identity has been verified successfully.
                </p>
              </>
            ) : kycStatus.status === 'pending' ? (
              <>
                <div className="w-24 h-24 mx-auto rounded-full bg-yellow-900/30 border-2 border-yellow-800 flex items-center justify-center mb-6">
                  <LoadingSpinner size="xl" color="yellow" />
                </div>
                <h1 className="text-3xl font-bold text-white mb-4">⏳ Verification Pending</h1>
                <p className="text-gray-300 text-lg mb-8">
                  Your KYC submission is under review. We'll notify you once completed.
                </p>
              </>
            ) : (
              <>
                <div className="w-24 h-24 mx-auto rounded-full bg-red-900/30 border-2 border-red-800 flex items-center justify-center mb-6">
                  <FiX className="w-12 h-12 text-red-400" />
                </div>
                <h1 className="text-3xl font-bold text-white mb-4">❌ Verification Rejected</h1>
                <p className="text-gray-300 text-lg mb-8">
                  {kycStatus.rejection_reason || 'Please resubmit with correct documents.'}
                </p>
                <button
                  onClick={() => setStep(1)}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg font-semibold transition-all"
                >
                  Resubmit KYC
                </button>
              </>
            )}
            
            <button
              onClick={() => navigate('/profile')}
              className="mt-6 px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
            >
              Back to Profile
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-black pt-16">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <FiArrowLeft className="w-5 h-5" />
            Back to Profile
          </button>
          
          <h1 className="text-3xl font-bold text-white mb-2">🔐 KYC Verification</h1>
          <p className="text-gray-400">
            Complete verification to unlock full platform access
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex justify-between items-center relative mb-2">
            <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-800 -z-10"></div>
            <div 
              className="absolute top-4 left-0 h-0.5 bg-gradient-to-r from-blue-600 to-cyan-500 -z-10 transition-all duration-300"
              style={{ width: `${((step - 1) / 2) * 100}%` }}
            ></div>
            
            {[1, 2, 3].map((stepNumber) => (
              <div key={stepNumber} className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                  stepNumber <= step 
                    ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white' 
                    : 'bg-gray-800 text-gray-400'
                }`}>
                  {stepNumber}
                </div>
                <span className="mt-2 text-xs font-medium">
                  {stepNumber === 1 && 'Document Info'}
                  {stepNumber === 2 && 'Upload'}
                  {stepNumber === 3 && 'Address'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {errors.general && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
            <div className="flex items-center gap-3">
              <FiAlertCircle className="w-5 h-5 text-red-400" />
              <span className="text-red-400">{errors.general}</span>
            </div>
          </div>
        )}

        {/* Step Components */}
        {step === 1 && (
          <KycStep1
            formData={formData}
            errors={errors}
            onInputChange={handleInputChange}
            countries={countries}
          />
        )}
        
        {step === 2 && (
          <KycStep2
            formData={formData}
            files={files}
            errors={errors}
            onInputChange={handleInputChange}
            onFileChange={handleFileChange}
            onPrevStep={prevStep}
          />
        )}
        
        {step === 3 && (
          <KycStep3
            formData={formData}
            files={files}
            errors={errors}
            submitting={submitting}
            onInputChange={handleInputChange}
            onFileChange={handleFileChange}
            onPrevStep={prevStep}
            onSubmit={handleSubmit}
            countries={countries}
          />
        )}

        {/* Step Navigation (for step 1 only) */}
        {step === 1 && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={nextStep}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg font-semibold transition-all"
            >
              Continue to Upload
            </button>
          </div>
        )}

        {/* Step Navigation (for step 2 only) */}
        {step === 2 && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={nextStep}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg font-semibold transition-all"
            >
              Continue to Address
            </button>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default KycVerification;